import { ExternalLink, FileText, Receipt } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { useBillingInvoices } from "@/hooks/useSubscriptions";
import { formatDate } from "@/lib/utils";
import type { BillingInvoiceDto } from "@/types/api";

/**
 * Real payment history pulled live from Stripe — the actual amounts charged,
 * with links to each hosted invoice and PDF. Distinct from the local
 * subscription history above (which is what was ordered, not what was billed).
 */
export function InvoicesCard() {
  const { data, isLoading, error } = useBillingInvoices();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5 text-brand-accentDark" /> Payments & invoices
        </CardTitle>
        <CardDescription>
          Your actual charges, live from Stripe — newest first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : error ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Couldn't load payments. Try again later.
          </p>
        ) : !data || data.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Desktop ≥ md */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Invoice</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map(inv => (
                    <TableRow key={inv.id}>
                      <TableCell className="whitespace-nowrap">{formatDate(inv.created)}</TableCell>
                      <TableCell className="max-w-[280px] truncate">{inv.description || inv.number || "—"}</TableCell>
                      <TableCell><StatusBadge status={inv.status} paid={inv.paid} /></TableCell>
                      <TableCell className="whitespace-nowrap text-right font-medium">{money(inv.amount, inv.currency)}</TableCell>
                      <TableCell className="text-right">
                        <InvoiceLinks inv={inv} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile */}
            <ul className="space-y-3 md:hidden">
              {data.map(inv => (
                <li key={inv.id} className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold">{money(inv.amount, inv.currency)}</span>
                    <StatusBadge status={inv.status} paid={inv.paid} />
                  </div>
                  <p className="mt-1 truncate text-sm text-muted-foreground">{inv.description || inv.number || "—"}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{formatDate(inv.created)}</span>
                    <InvoiceLinks inv={inv} />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function InvoiceLinks({ inv }: { inv: BillingInvoiceDto }) {
  return (
    <span className="inline-flex items-center gap-3">
      {inv.hosted_invoice_url && (
        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-accent hover:underline">
          <ExternalLink className="h-3.5 w-3.5" /> View
        </a>
      )}
      {inv.invoice_pdf && (
        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
          <FileText className="h-3.5 w-3.5" /> PDF
        </a>
      )}
    </span>
  );
}

function StatusBadge({ status, paid }: { status: string | null; paid: boolean }) {
  const label = status ?? (paid ? "paid" : "—");
  if (paid || status === "paid") return <Badge variant="success" className="capitalize">{label}</Badge>;
  return <Badge variant="outline" className="capitalize">{label}</Badge>;
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center text-muted-foreground">
      <Receipt className="h-8 w-8" />
      <p className="text-sm">No payments yet. Invoices appear here after your first checkout.</p>
    </div>
  );
}

function money(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "USD").toUpperCase() }).format(amount);
  } catch {
    return `${(currency || "USD").toUpperCase()} ${amount.toFixed(2)}`;
  }
}
